import { useRef } from 'react';
import { PageContainer, ProTable, type ActionType, type ProColumns } from '@ant-design/pro-components';
import { Button, Popconfirm, Space, Tag, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { listIndustries, deleteIndustry, type Industry, type IndustryStatus } from '@/api/industries';

const STATUS_TAG: Record<IndustryStatus, { color: string; text: string }> = {
  ACTIVE: { color: 'green', text: '活跃' },
  INACTIVE: { color: 'default', text: '未激活' },
  COMING_SOON: { color: 'blue', text: '即将上线' },
  DEPRECATED: { color: 'red', text: '已弃用' },
};

export default function IndustryListPage() {
  const navigate = useNavigate();
  const actionRef = useRef<ActionType>();

  const columns: ProColumns<Industry>[] = [
    {
      title: '行业代码',
      dataIndex: 'code',
      width: 140,
      copyable: true,
    },
    {
      title: '名称',
      dataIndex: 'name',
      ellipsis: true,
      render: (_, r) => r.display_name || r.name,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 120,
      valueType: 'select',
      valueEnum: {
        ACTIVE: { text: '活跃', status: 'Success' },
        INACTIVE: { text: '未激活', status: 'Default' },
        COMING_SOON: { text: '即将上线', status: 'Processing' },
        DEPRECATED: { text: '已弃用', status: 'Error' },
      },
      render: (_, r) => {
        const tag = STATUS_TAG[r.status];
        return <Tag color={tag.color}>{tag.text}</Tag>;
      },
    },
    {
      title: '颜色',
      dataIndex: 'color',
      width: 100,
      search: false,
      render: (_, r) => (
        <Space>
          <span
            style={{
              display: 'inline-block',
              width: 16,
              height: 16,
              borderRadius: 4,
              background: r.color,
              border: '1px solid #d9d9d9',
            }}
          />
          <span style={{ fontFamily: 'monospace' }}>{r.color}</span>
        </Space>
      ),
    },
    {
      title: '用户数',
      dataIndex: 'user_count',
      width: 100,
      search: false,
      sorter: true,
    },
    {
      title: '操作',
      valueType: 'option',
      width: 160,
      fixed: 'right',
      render: (_, r) => [
        <a key="edit" onClick={() => navigate(`/industries/${r.id}/edit`)}>
          编辑
        </a>,
        <Popconfirm
          key="del"
          title="确定删除该行业吗?"
          onConfirm={async () => {
            try {
              await deleteIndustry(r.id);
              message.success('删除成功');
              actionRef.current?.reload();
            } catch (e) {
              // 拦截器已提示
            }
          }}
        >
          <a style={{ color: '#ff4d4f' }}>删除</a>
        </Popconfirm>,
      ],
    },
  ];

  return (
    <PageContainer header={{ title: '行业管理' }}>
      <ProTable<Industry>
        actionRef={actionRef}
        columns={columns}
        rowKey="id"
        scroll={{ x: 900 }}
        request={async (params) => {
          const { current, pageSize, ...rest } = params;
          try {
            const resp = await listIndustries({
              page: current,
              page_size: pageSize,
              status: rest.status as IndustryStatus | undefined,
              search: rest.code || rest.name,
            });
            return {
              data: resp.results || resp.data || [],
              success: true,
              total: resp.pagination?.count ?? 0,
            };
          } catch {
            return { data: [], success: false, total: 0 };
          }
        }}
        toolBarRender={() => [
          <Button
            key="add"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/industries/new')}
          >
            新建行业
          </Button>,
        ]}
        search={{ labelWidth: 'auto' }}
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />
    </PageContainer>
  );
}
